import React, { useEffect, useState } from 'react';
import { createGlobalStyle } from 'styled-components';
import { useSelector, useStore } from 'react-redux';
import dynamic from 'next/dynamic';
import { Loader } from '@components/Loader';
import { ErrorStub } from '@components/Error-Stub';
import { blueboxStyles } from '@components/Bluebox';
import { ctaboxStyles } from '@components/CTA-box';
import { quoteStyles } from '@components/Quote';
import { selfCheckStyles } from '@components/Self-Check';
import { listItemStyles } from '@components/List-Item';
import { UI_VERSION, IS_CLIENT, HOSTNAME, POP_IS_IFRAME_VIEW } from '@constants';
import { ON_CHANGE_LANG } from '@constants/observer';
import { Global } from '@modules/global';
import { getErrorContent, LocalStorage } from '@libs';
import { MainHead } from './Main-Head';
import { Header } from './Header';
import { Footer } from './Footer';
import { ui } from './ui';
import { useTranslations } from '@libs/translations';
import { isHeaderBannerVisible } from '@components/Banners/helpers';

const Cookies = dynamic(() => import('../components/Cookies').then((mod) => mod.Cookies), { ssr: false });

const MAX_WINDOW_WIDTH_BREACKPOINT = {
  mobile: 1023,
  tablet: 1279,
};

const WINDOW_WIDTH_VALUES = {
  mobile: 'mobile',
  tablet: 'tablet',
  laptop: 'laptop',
};

export const MainLayout = ({
  title,
  description,
  keywords,
  processing = '',
  meta,
  alternateLinks,
  ogImage,
  ogType,
  children,
  isDarkMode = false,
  isTransparentHeader = false,
  reqQuery = '',
  isNoFixedHeader = false,
  isHideOnScrollHeader = false,
  wideHeader = false,
}) => {
  const store = useStore();
  const translations = useTranslations();
  const { locale } = useSelector((state) => state.settings);
  const isUser = Boolean(useSelector((state) => state.user));
  const { errors = {}, showBanners = {}, common = {} } = useSelector((state) => state);
  const error = errors[processing];
  const errorContent = error ? getErrorContent(error) : null;
  const isShowHeaderBanner = isHeaderBannerVisible(common.banner, showBanners);
  const [previewIframe, setPreviewIframe] = useState(reqQuery['iframe-preview'] === '1');

  const checkPreviewIframe = () => {
    if (IS_CLIENT) {
      const isIframeView = LocalStorage.get(POP_IS_IFRAME_VIEW) === true;

      if (isIframeView) {
        setPreviewIframe(true);

        return;
      }
    }

    if (!reqQuery) return;

    const isIframeView = reqQuery['iframe-preview'] === '1';

    setPreviewIframe(isIframeView);

    if (IS_CLIENT && isIframeView) {
      LocalStorage.set(POP_IS_IFRAME_VIEW, true);
    }
  };

  useEffect(() => {
    checkPreviewIframe();

    if (IS_CLIENT) {
      Global.saveStore(store);

      const script = document.createElement('script');
      script.src = `https://ui.${HOSTNAME}/static/js/site.js?v${UI_VERSION}`;
      //script.src = `http://localhost:9999/ui`;
      script.async = true;
      script.defer = true;

      document.head.appendChild(script);
      script.onload = () => {
        ui.connectTranslations();
        ui.initObserver({ locale });
        ui.initFormEntry({ locale });
        ui.initFormEntryListeners({ locale });
        ui.initUserWidget({ locale });
        ui.initGallery({ locale });
        ui.initGalleryListeners();
        ui.initImageEditor({ locale });
        ui.initOpenImageEditor();
        ui.initStore({ locale });
        ui.initStoreListeners();
        ui.initNotifications();
      };

      // обновляем те модули, которые зависят от языка
      ui.addEventListener(ON_CHANGE_LANG, () => {
        ui.reinitUserWidget({ locale, isUser });
        ui.reinitStore({ locale });
        ui.reinitGallery({ locale });
        ui.reinitFormEntry({ locale });
      });

      if (locale === 'ru') {
        const _tmr = window._tmr || (window._tmr = []);

        _tmr.push({ id: '3426878', type: 'pageView', start: new Date().getTime() });

        (function (d, w, id) {
          if (d.getElementById(id)) return;

          const ts = d.createElement('script');

          ts.type = 'text/javascript';
          ts.async = true;
          ts.id = id;
          ts.src = 'https://top-fwz1.mail.ru/js/code.js';

          const f = function () {
            const s = d.getElementsByTagName('script')[0];

            s.parentNode.insertBefore(ts, s);
          };

          if (w.opera === '[object Opera]') {
            d.addEventListener('DOMContentLoaded', f, false);
          } else {
            f();
          }
        })(document, window, 'tmr-code');
        /* Top.Mail.Ru counter */
      }
    }
  }, []);

  const [windowWidth, setWindowWidth] = useState(WINDOW_WIDTH_VALUES.mobile);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= MAX_WINDOW_WIDTH_BREACKPOINT.mobile) {
        setWindowWidth(WINDOW_WIDTH_VALUES.mobile);
      } else if (window.innerWidth <= MAX_WINDOW_WIDTH_BREACKPOINT.tablet) {
        setWindowWidth(WINDOW_WIDTH_VALUES.tablet);
      } else {
        setWindowWidth(WINDOW_WIDTH_VALUES.laptop);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  let paddingTopMain;
  switch (windowWidth) {
    case WINDOW_WIDTH_VALUES.mobile:
      paddingTopMain = isShowHeaderBanner ? '75px' : '35px';
      break;
    case WINDOW_WIDTH_VALUES.tablet:
      paddingTopMain = isShowHeaderBanner ? '80px' : '40px';
      break;
    default:
      paddingTopMain = isShowHeaderBanner ? '80px' : '40px';
  }

  paddingTopMain = isTransparentHeader ? `calc(${paddingTopMain} - 40px)` : paddingTopMain;

  return (
    <>
      <GlobalStyle />
      <MainHead
        title={!error ? title : translations.t(errorContent.title)}
        description={description}
        keywords={keywords}
        meta={meta}
        alternateLinks={alternateLinks}
        ogImage={ogImage}
        ogType={ogType}
      />
      {!previewIframe && (
        <Header
          isDarkMode={isDarkMode}
          isTransparent={isTransparentHeader}
          isNoFixed={isNoFixedHeader}
          isHideOnScroll={isHideOnScrollHeader}
          wideHeader={wideHeader}
        />
      )}
      <main style={{ paddingTop: previewIframe ? 0 : paddingTopMain }}>
        {error ? <ErrorStub error={errorContent} /> : children}
      </main>
      <div id="store" />
      <div id="gallery" />
      <div id="image-editor" />
      <div id="modal" />
      <div id="notifications" />
      {!previewIframe && <Footer />}
      <Loader />
      <Cookies />
    </>
  );
};

const GlobalStyle = createGlobalStyle`
  .bluebox {
    ${blueboxStyles.container};
  }

  .ctabox {
    ${ctaboxStyles.container};
  }

  .self-check {
    ${selfCheckStyles.container};
  }
  
  .list {
    ${listItemStyles.container};
  }
  
  .list__number {
    ${listItemStyles.number};
    ${listItemStyles.number_theme_primary};
  }
  
  .list__content {
    ${listItemStyles.content};
  }
  .swiper-cards .swiper-slide {
    border-radius: 11px;
  }

  .quote {
    ${quoteStyles.container};
  }
`;
